/**
 * /game/:id — screens 3d (live) and 3e (recap).
 *
 * The game's own `state` picks the layout: `in` gets the filled header, the
 * linescore, the situation card with a win-probability bar, and the running
 * scoring list; `post` gets the neutral header and the recap. A game that
 * hasn't kicked off yet gets the neutral header too, with the slate context it
 * actually has — never a blanked-out live layout.
 *
 * D35 is the hard rule on the recap: exactly one outbound link, no embedded
 * player, no thumbnails, no scraped video URLs.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import type { Challenge, Game as GameT, RootingTeam, ScoringPlay, WeekRef } from '@shared/types'
import { teamAbbr, teamName } from '@shared/teams'
import { useStore } from '../lib/store'
import { useTheme } from '../theme/ThemeProvider'
import { ApiFailure, GET } from '../lib/api'
import {
  Body, Button, Card, Eyebrow, Freshness, Header, Notice, Screen, SkeletonCard, Spacer, TeamLogo,
} from '../ui'
import { MatchupCard } from '../ui/MatchupCard'
import { ExampleChallenges } from '../ui/ExampleChallenges'
import { GameRooting } from '../ui/Rooting'

const MINUTE = 60_000
const HHMM = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', hour12: true })
const DAY_TIME = new Intl.DateTimeFormat(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit', hour12: true })

interface GameDetail {
  game: GameT
  week: WeekRef | null
  linescores: { home: number[]; away: number[] } | null
  scoringPlays: ScoringPlay[]
  leaders: { category: string; teamId: string | null; name: string; value: string }[]
  odds: { spread: number | null; overUnder: number | null; homeWinProb: number | null; favoriteTeamId: string | null }
  challenges: Challenge[]
  /** Away first, then home — the two teams' records and who supports them. */
  rooting?: RootingTeam[]
  staleAsOf: number | null
}

function periodLabel(period: number | null): string {
  if (!period) return ''
  if (period <= 4) return `Q${period}`
  return period === 5 ? 'OT' : `OT${period - 4}`
}

/** D36 — freshness is computed from the last upstream sync, not from render. */
function healthOf(asOf: number | null): 'ok' | 'stale' | 'paused' | 'unavailable' {
  if (asOf === null) return 'unavailable'
  const age = Date.now() - asOf
  if (age < 2 * MINUTE) return 'ok'
  if (age < 10 * MINUTE) return 'stale'
  return 'paused'
}

function titleCase(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

/**
 * The single outbound link (D35). NFL.com's scores pages are addressed by
 * season and week, which is everything we have and nothing we scraped.
 */
function highlightsUrl(week: WeekRef | null): string {
  if (!week) return 'https://www.nfl.com/scores/'
  const bucket = week.seasonType === 3 ? 'POST' : week.seasonType === 1 ? 'PRE' : 'REG'
  return `https://www.nfl.com/scores/${week.seasonYear}/${bucket}${week.weekNumber}`
}

export default function Game() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { state } = useStore()
  const { theme } = useTheme()
  const myTeamId = state?.user.teamId ?? null

  const [data, setData] = useState<GameDetail | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [notFound, setNotFound] = useState(false)
  const liveRef = useRef(false)

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!id) return
    try {
      const res = await GET<GameDetail>(`/api/games/${encodeURIComponent(id)}`, signal)
      setData(res)
      liveRef.current = res.game.state === 'in'
      setErr(null)
      setNotFound(false)
    } catch (e) {
      if (signal?.aborted) return
      if (e instanceof ApiFailure && e.code === 'not_found') { setNotFound(true); return }
      // Last-known-good stays on screen (D36 — a stale score beats no score).
      setErr(e instanceof ApiFailure ? e.message : 'Couldn’t refresh this game.')
    }
  }, [id])

  useEffect(() => {
    const ac = new AbortController()
    setData(null)
    void load(ac.signal)
    return () => ac.abort()
  }, [load])

  // While the game is live, patch in place on a slow timer (D37 — no reloads).
  useEffect(() => {
    const t = setInterval(() => {
      if (!liveRef.current) return
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
      void load()
    }, 20_000)
    return () => clearInterval(t)
  }, [load])

  if (notFound) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header logo={false} left={<BackButton label="← Back" onClick={() => navigate('/schedule')} />} />
        <Body>
          <Card style={{ padding: '26px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>That game isn’t here.</div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              It may not have synced yet. Try the schedule.
            </div>
          </Card>
        </Body>
      </Screen>
    )
  }

  if (!data) {
    return (
      <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
        <Header logo={false} left={<BackButton label="← Back" onClick={() => navigate(-1)} />} />
        <Body>
          <SkeletonCard lines={3} />
          <Spacer h={12} />
          <SkeletonCard lines={4} />
          <Spacer h={12} />
          <SkeletonCard lines={3} />
        </Body>
      </Screen>
    )
  }

  const shared = { data, err, myTeamId, onBack: () => navigate(-1) }

  return data.game.state === 'in'
    ? <LiveGame {...shared} inkSoft={`${theme.ink}C7`} />
    : <RecapGame {...shared} />
}

function BackButton({ label, onClick, filled }: { label: string; onClick: () => void; filled?: boolean }) {
  return (
    <button
      onClick={onClick}
      aria-label="Back"
      className="mono"
      style={{
        fontSize: 11, minHeight: 44, minWidth: 44, textAlign: 'left',
        color: filled ? 'inherit' : 'var(--dim)',
        opacity: filled ? 0.8 : 1,
      }}
    >
      {label}
    </button>
  )
}

// ════════════════════════════════════════════════════════════════════════════
// 3d — live
// ════════════════════════════════════════════════════════════════════════════

function LiveGame({
  data, err, myTeamId, onBack, inkSoft,
}: {
  data: GameDetail; err: string | null; myTeamId: string | null; onBack: () => void; inkSoft: string
}) {
  const navigate = useNavigate()
  const g = data.game
  const homeAbbr = teamAbbr(g.homeTeamId)
  const awayAbbr = teamAbbr(g.awayTeamId)

  // The prototype's grid is four quarters plus a total. Overtime is rare, and
  // folding OT points into Q4 would be a lie, so the grid grows by one column
  // in that case and keeps every other metric identical.
  const periods = Math.max(
    4,
    data.linescores?.home.length ?? 0,
    data.linescores?.away.length ?? 0,
  )
  const grid: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: `1fr repeat(${periods}, 34px) 42px`,
    gap: 6,
  }
  const periodHeadings = Array.from({ length: periods }, (_, i) =>
    i < 4 ? String(i + 1) : i === 4 ? 'OT' : `OT${i - 3}`)

  const situation = g.situation
  const possessionLine = situation
    ? [situation.possessionText, situation.downDistanceText].filter(Boolean).join(' · ')
    : null

  // Win probability is always the home team's; show it from the viewer's side
  // when the viewer's team is playing, otherwise from the home team's.
  const refTeamId = myTeamId && (g.homeTeamId === myTeamId || g.awayTeamId === myTeamId)
    ? myTeamId
    : g.homeTeamId
  const winPct = g.homeWinProb === null
    ? null
    : Math.round(refTeamId === g.homeTeamId ? g.homeWinProb : 100 - g.homeWinProb)

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        filled
        logo={false}
        left={<BackButton filled label={`← ${data.week?.label ?? 'Back'}`} onClick={onBack} />}
        right={
          <Freshness asOf={data.staleAsOf} health={healthOf(data.staleAsOf)} onFill />
        }
        sub={
          <div
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginTop: 10 }}
            aria-live="polite"
          >
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <TeamLogo teamId={g.awayTeamId} size={32} fallback="none" />
                <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.08em' }}>{awayAbbr}</span>
              </div>
              <div className="numerals" style={{ fontSize: 52, fontWeight: 800, lineHeight: 1, marginTop: 2 }}>
                {g.awayScore ?? 0}
              </div>
            </div>
            <div className="mono" style={{ fontSize: 12, textAlign: 'center', opacity: 0.9 }}>
              {[periodLabel(g.period), g.clockDisplay].filter(Boolean).join(' ') || g.statusDetail || 'Live'}
            </div>
            <div style={{ textAlign: 'right', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: 'row-reverse' }}>
                <TeamLogo teamId={g.homeTeamId} size={32} fallback="none" />
                <span style={{ fontSize: 15, fontWeight: 700, letterSpacing: '.08em' }}>{homeAbbr}</span>
              </div>
              <div className="numerals" style={{ fontSize: 52, fontWeight: 800, lineHeight: 1, marginTop: 2 }}>
                {g.homeScore ?? 0}
              </div>
            </div>
          </div>
        }
      />

      <Body>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={16} /></> : null}

        {/* linescore */}
        <div className="mono" style={{ ...grid, fontSize: 12, color: 'var(--dim)' }}>
          <span />
          {periodHeadings.map((h) => <span key={h} style={{ textAlign: 'center' }}>{h}</span>)}
          <span style={{ textAlign: 'center' }}>T</span>
        </div>
        <LinescoreRow
          abbr={awayAbbr}
          values={data.linescores?.away ?? []}
          periods={periods}
          total={g.awayScore ?? 0}
          grid={grid}
          style={{ borderTop: '1px solid var(--line)', marginTop: 8 }}
        />
        <LinescoreRow
          abbr={homeAbbr}
          values={data.linescores?.home ?? []}
          periods={periods}
          total={g.homeScore ?? 0}
          grid={grid}
          style={{ borderTop: '1px solid var(--line)', borderBottom: '1px solid var(--line)' }}
        />

        {/* situation */}
        {possessionLine || situation?.lastPlayText || winPct !== null ? (
          <Card style={{ marginTop: 18 }}>
            {possessionLine ? (
              <div style={{ fontSize: 14, fontWeight: 600 }}>🏈 {possessionLine}</div>
            ) : null}
            {situation?.lastPlayText ? (
              <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: possessionLine ? 8 : 0, lineHeight: 1.45 }}>
                {situation.lastPlayText}
              </div>
            ) : null}
            {winPct !== null ? (
              <>
                <div
                  role="progressbar"
                  aria-valuenow={winPct}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${teamName(refTeamId)} win probability`}
                  style={{ marginTop: 14, height: 6, borderRadius: 3, background: 'var(--line)', overflow: 'hidden' }}
                >
                  <div style={{ height: '100%', width: `${winPct}%`, background: 'var(--bright)', transition: 'width 300ms ease' }} />
                </div>
                <div className="mono" style={{ marginTop: 6, fontSize: 11, color: 'var(--dim)' }}>
                  {teamAbbr(refTeamId)} {winPct}% win
                </div>
              </>
            ) : null}
          </Card>
        ) : null}

        {/* scoring plays */}
        {data.scoringPlays.length ? (
          <>
            <Eyebrow style={{ marginTop: 20 }}>Scoring plays</Eyebrow>
            {[...data.scoringPlays].reverse().map((p) => <ScoringRow key={p.id} play={p} />)}
          </>
        ) : null}

        {/* challenges */}
        <Card style={{ marginTop: 20 }}>
          <Eyebrow>Challenges on this game</Eyebrow>
          {data.challenges.length ? (
            data.challenges.slice(0, 4).map((c, i) => (
              <div
                key={c.id}
                style={{ fontSize: 14, marginTop: i === 0 ? 10 : 6, color: c.viewer.isParticipant ? 'var(--fg)' : 'var(--dim)' }}
              >
                {challengeLine(c)}
              </div>
            ))
          ) : (
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 10 }}>
              Nothing running on this one yet. Someone has to go first.
            </div>
          )}
          <Button
            compact
            onClick={() => navigate(`/challenges/new?game=${encodeURIComponent(g.id)}`)}
            style={{ marginTop: 14, borderRadius: 12, fontSize: 13, fontWeight: 700 }}
          >
            Start a challenge on this game
          </Button>
        </Card>

        <ExampleChallenges home={homeAbbr} away={awayAbbr} style={{ marginTop: 26 }} />
      </Body>
    </Screen>
  )
}

function LinescoreRow({
  abbr, values, periods, total, grid, style,
}: {
  abbr: string; values: number[]; periods: number; total: number
  grid: CSSProperties; style?: CSSProperties
}) {
  return (
    <div className="mono" style={{ ...grid, fontSize: 13, padding: '10px 0', ...style }}>
      <span>{abbr}</span>
      {Array.from({ length: periods }, (_, i) => (
        <span key={i} style={{ textAlign: 'center', color: values[i] === undefined ? 'var(--dim)' : 'var(--fg)' }}>
          {values[i] === undefined ? '-' : values[i]}
        </span>
      ))}
      <span style={{ textAlign: 'center', fontWeight: 700 }}>{total}</span>
    </div>
  )
}

function ScoringRow({ play }: { play: ScoringPlay }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--line)' }}>
      <span className="mono" style={{ fontSize: 11, color: 'var(--dim)', width: 50, flex: '0 0 auto' }}>
        {[periodLabel(play.period), play.clockDisplay].filter(Boolean).join(' ')}
      </span>
      <span style={{ fontSize: 13, lineHeight: 1.4, minWidth: 0 }}>
        {teamAbbr(play.teamId)} · {play.text}
      </span>
    </div>
  )
}

function challengeLine(c: Challenge): string {
  const a = c.sides.a.map((p) => p.displayName).join(' & ') || 'nobody yet'
  const b = c.sides.b.map((p) => p.displayName).join(' & ') || 'nobody yet'
  const left = c.viewer.side === 'a' ? 'You' : a
  const right = c.viewer.side === 'b' ? 'You' : b
  return `${left} vs ${right} · ${c.stakeLabel}`
}

// ════════════════════════════════════════════════════════════════════════════
// 3e — recap (and the pre-kickoff case)
// ════════════════════════════════════════════════════════════════════════════

function RecapGame({
  data, err, myTeamId, onBack,
}: {
  data: GameDetail; err: string | null; myTeamId: string | null; onBack: () => void
}) {
  const navigate = useNavigate()
  const g = data.game
  const final = g.state === 'post'
  const homeAbbr = teamAbbr(g.homeTeamId)
  const awayAbbr = teamAbbr(g.awayTeamId)

  const homeScore = g.homeScore ?? 0
  const awayScore = g.awayScore ?? 0
  const homeLost = final && homeScore < awayScore
  const awayLost = final && awayScore < homeScore

  const headerNote = final
    ? `${data.week?.label ?? 'Final'} · ${g.wentToOt ? 'Final/OT' : 'Final'}`
    : `${data.week?.label ?? 'Kickoff'} · ${DAY_TIME.format(g.kickoffAt)}`

  const leaders = useMemo(
    () => data.leaders.filter((l) => l.value),
    [data.leaders],
  )

  return (
    <Screen style={{ flex: '1 1 auto', minHeight: 0 }}>
      <Header
        logo={false}
        left={<BackButton label={`← ${headerNote}`} onClick={onBack} />}
      />

      <Body>
        {err ? <><Notice tone="warn">{err}</Notice><Spacer h={16} /></> : null}

        {/* The scoreboard as a card rather than header text: the logos are the
            fastest way to read a matchup, and before kickoff there is no score
            for the numerals to carry. */}
        <MatchupCard game={g} myTeamId={myTeamId} style={{ marginBottom: 18 }} />

        {!final ? (
          <Card style={{ padding: '26px 18px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {awayAbbr} at {homeAbbr} hasn’t kicked off.
            </div>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 6 }}>
              Kickoff {DAY_TIME.format(g.kickoffAt)}
            </div>
          </Card>
        ) : null}

        {data.scoringPlays.length ? (
          <>
            <Eyebrow style={{ marginTop: final ? 0 : 20 }}>
              {final ? 'Every scoring play' : 'Scoring plays'}
            </Eyebrow>
            {data.scoringPlays.map((p) => <ScoringRow key={p.id} play={p} />)}
          </>
        ) : final ? (
          <>
            <Eyebrow>Every scoring play</Eyebrow>
            <div style={{ fontSize: 13, color: 'var(--dim)', marginTop: 12 }}>
              No scoring plays came through for this game.
            </div>
          </>
        ) : null}

        {leaders.length ? (
          <>
            <Eyebrow style={{ marginTop: 22 }}>Leaders</Eyebrow>
            {leaders.map((l, i) => (
              <div
                key={`${l.category}-${i}`}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
                  padding: '11px 0', borderBottom: '1px solid var(--line)', fontSize: 13,
                }}
              >
                <div style={{ color: 'var(--dim)', minWidth: 0 }}>
                  {titleCase(l.category)}
                  {l.name ? <div style={{ fontSize: 11, marginTop: 2 }}>{l.name}</div> : null}
                </div>
                <span style={{ flex: '0 0 auto', textAlign: 'right' }}>{l.value}</span>
              </div>
            ))}
          </>
        ) : null}

        <GameRooting sides={data.rooting ?? []} style={{ marginTop: 22 }} />

        {data.challenges.length ? (
          <Card style={{ marginTop: 22 }}>
            <Eyebrow>Challenges on this game</Eyebrow>
            {data.challenges.slice(0, 4).map((c, i) => (
              <div
                key={c.id}
                style={{ fontSize: 14, marginTop: i === 0 ? 10 : 6, color: c.viewer.isParticipant ? 'var(--fg)' : 'var(--dim)' }}
              >
                {challengeLine(c)}
              </div>
            ))}
            <Button
              compact
              variant="secondary"
              onClick={() => navigate('/challenges')}
              style={{ marginTop: 14, borderRadius: 12, fontSize: 13 }}
            >
              View challenges
            </Button>
          </Card>
        ) : null}

        {/* D35 — one outbound link. No player, no thumbnails, no scraped URLs. */}
        {final ? (
          <a
            href={highlightsUrl(data.week)}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'block', marginTop: 22, textAlign: 'center', padding: 14,
              borderRadius: 12, border: '1px solid var(--line)',
              fontSize: 13, fontWeight: 600, color: 'var(--bright)', minHeight: 48,
            }}
          >
            Watch highlights on NFL.com ↗
          </a>
        ) : null}

        {/*
          The way in, above the examples rather than below them.

          The examples exist to show what a feat can be, and somebody who has
          read one has already decided — but until now the only thing to do
          with that decision was to leave for the Feats tab and pick this game
          again from the list. The game-day view has had this button inside its
          challenges card all along; this is the same door on the screen people
          actually read during the week.
        */}
        <Button
          onClick={() => navigate(`/challenges/new?game=${encodeURIComponent(g.id)}`)}
          style={{ marginTop: 26 }}
        >
          Challenge someone
        </Button>

        <ExampleChallenges home={homeAbbr} away={awayAbbr} style={{ marginTop: 22 }} />
      </Body>
    </Screen>
  )
}
